/**
 * Synapsy Widget - Iframe Container
 * Cria o iframe que carrega a aplicação React
 */

import { getIframeStyles } from "./styles.js";
import { resolveUserId, resolveSession } from "./identity.js";

/**
 * Cria o iframe container
 * @param {Object} config - Configuração do widget
 * @returns {HTMLIFrameElement} Elemento iframe
 */
export function createIframe(config) {
  const iframe = document.createElement("iframe");
  iframe.id = "synapsy-chat-frame";
  iframe.className = "synapsy-iframe";
  iframe.title = "Synapsy Chat";

  try {
    const url = new URL(config.iframeUrl);
    url.searchParams.set("site_domain", window.location.hostname);
    url.searchParams.set("page_url", window.location.href);
    // Identidade resolvida no host (first-party): sobrevive a reloads/navegações
    // e é lida pelo tracker.ts dentro do iframe (onde o storage é bloqueado).
    url.searchParams.set("cortexy_user_id", resolveUserId(config.userId));
    url.searchParams.set("cortexy_session", resolveSession());
    iframe.src = url.toString();
  } catch (error) {
    console.error("[Synapsy Widget] iframeUrl inválida:", config.iframeUrl, error);
    iframe.remove();
    return iframe;
  }
  iframe.style.cssText = getIframeStyles(config.position);
  iframe.style.display = "none";

  // Sandbox: allow-popups + allow-popups-to-escape-sandbox são necessários para links
  // target="_blank" abrirem em uma nova aba que não herda o sandbox — sem a flag de escape,
  // a nova aba vira "sandboxed top-level browsing context" e o Chrome aplica X-Frame-Options
  // mesmo em navegação top-level, bloqueando sites como api.whatsapp.com (X-Frame-Options: DENY).
  //
  // allow-downloads é o "Baixar" da foto do "Descubra seu Destino": ele clica um
  // `<a download href="blob:…">`, e num iframe sandboxed sem essa flag o Chrome recusa em
  // silêncio ("Download is disallowed. The frame … is sandboxed"). Também é o caminho de
  // FALLBACK do "Compartilhar", então sem ela as duas ações da recompensa morrem juntas.
  iframe.setAttribute(
    "sandbox",
    "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads",
  );

  // Permissions Policy: a allowlist padrão de `camera` e de `web-share` é `self`, ou seja, só o
  // site que hospeda o widget. Sem delegar aqui, os dois recursos do "Descubra seu Destino" são
  // bloqueados pela policy dentro do iframe — invisível em qualquer teste feito pelo link direto
  // do QR, onde a app roda em top-level:
  //
  //   - sem `camera`, o `getUserMedia` do overlay de captura falha e "Tirar foto agora" cai
  //     direto no estado de erro;
  //   - sem `web-share`, `navigator.canShare({files})` devolve `false` por policy (não por falta
  //     de suporte), e "Compartilhar" cai calado no fallback de download — perdendo justamente o
  //     compartilhamento que o sorteio pede.
  //
  // O nome sozinho é a forma curta de `<recurso> 'src'`: vale só para a origem do `src` deste
  // iframe, e não para o site inteiro. Sem `microphone` de propósito — a captura pede
  // `audio: false`, e pedir permissão que não se usa é o tipo de coisa que faz o browser
  // mostrar um aviso a mais e a pessoa recusar os dois. `web-share` não custa aviso nenhum: ela
  // não tem prompt, só habilita a folha nativa que o toque da pessoa já abre.
  iframe.setAttribute("allow", "camera; web-share");

  // Acessibilidade
  iframe.setAttribute("aria-label", "Chat de atendimento Synapsy");

  if (config.debug) {
    console.log("[Synapsy Widget] Iframe criado:", iframe.src);
  }

  return iframe;
}
